import { DocumentDetailFilterRequest } from '@mercator-gcp/types/elastic_search_service/documents/DocumentDetailFilterRequest';
import { DocumentDetailFilterResponse } from '@mercator-gcp/types/elastic_search_service/documents/DocumentDetailFilterResponse';
import { api } from 'hooks/useApi';
import type {
  CompanyFilterParameters,
  CompanyFilterResponse,
} from '@mercator-gcp/types/elastic_search_service/CompanyFilter';
import type { ProjectFilterRequest } from '@mercator-gcp/types/elastic_search_service/ProjectFilterRequest';
import type { AxiosResponse } from 'axios';
import type { PaginatedProject, PaginateReponse } from 'types';

type CompanyFilterRequestWithPage = CompanyFilterParameters & {
  page: number;
  sort?: string[];
};

const postFilterCompanies = ({
  name_search,
  project_city_codes,
  company_types,
  page,
  sort,
}: CompanyFilterRequestWithPage): Promise<AxiosResponse<CompanyFilterResponse>> => {
  return api.post(`filter-companies/?page=${page}`, {
    name_search,
    project_city_codes,
    company_types,
    ...(sort && { sort }),
  });
};

// Raw ES query bodies for a scoutbot-sourced saved search, keyed by index
// alias. Not part of the published ProjectFilterRequest schema - the
// backend reads it as an extra key rather than a declared field (see
// mercator-backend's ProjectsFilter/ProjectsAndDocumentsFilter views).
type RawQueries = Record<string, { query: Record<string, unknown> }>;

type ProjectFilterRequestWithPageAndSignal = ProjectFilterRequest & {
  page: number;
  signal?: AbortSignal;
  raw_queries?: RawQueries;
};

type ProjectFilterRequestWithSignal = ProjectFilterRequest & {
  signal?: AbortSignal;
  raw_queries?: RawQueries;
};

const postFilterProjects = ({
  companies,
  sort,
  page,
  mercator_metropolitan_ids,
  status,
  ...rest
}: ProjectFilterRequestWithPageAndSignal): Promise<
  AxiosResponse<PaginateReponse<PaginatedProject[]>>
> => {
  return api.post(`filter-projects/?page=${page}`, {
    companies,
    sort,
    mercator_metropolitan_ids,
    status,
    ...rest,
  });
};

const postFilterProjectsAndDocuments = ({
  companies,
  sort,
  page,
  mercator_metropolitan_ids,
  status,
  ...rest
}: ProjectFilterRequestWithPageAndSignal): Promise<
  AxiosResponse<PaginateReponse<PaginatedProject[]>>
> => {
  return api.post(`v2/filter-projects/?page=${page}`, {
    companies,
    sort,
    mercator_metropolitan_ids,
    status,
    ...rest,
  });
};

type Point = [number, number];
type Marker = { id: number; slug: string; address: string; coordinates: Point };
type FetchMarkersResponse = {
  data: {
    count: number;
    results?: Marker[];
  };
  message: string;
};

const postFilterProjectPins = (
  props: ProjectFilterRequestWithSignal,
): Promise<AxiosResponse<FetchMarkersResponse>> => {
  return api.post(`filter-projects/?pins=true`, props);
};

type DocumentDetailFilterRequestWithPage = DocumentDetailFilterRequest & {
  page: Number;
  point?: [number, number] | null;
  radius?: number | null;
};

const postFilterDocuments = ({
  page,
  document_type_codes,
  state_code,
  city_id,
  city_ids,
  city_code,
  allowed_city_ids,
  mercator_metropolitan_id,
  mercator_metropolitan_ids,
  latest_status,
  keyword_inclusions,
  keyword_exclusions,
  vertical_id_inclusions,
  vertical_id_exclusions,
  earliest_activity_end,
  earliest_activity_start,
  latest_activity_end,
  latest_activity_start,
  company_uuids,
  companies,
  sort,
  address,
  include_matching_project_ids,
  exclude_matching_project_ids,
  point,
  radius,
}: DocumentDetailFilterRequestWithPage): Promise<
  AxiosResponse<DocumentDetailFilterResponse>
> => {
  return api.post(`filter-documents/?page=${page}`, {
    document_type_codes,
    state_code,
    city_id,
    city_ids,
    city_code,
    allowed_city_ids,
    mercator_metropolitan_id,
    mercator_metropolitan_ids,
    latest_status,
    keyword_inclusions,
    keyword_exclusions,
    vertical_id_inclusions,
    vertical_id_exclusions,
    earliest_activity_end,
    earliest_activity_start,
    latest_activity_end,
    latest_activity_start,
    company_uuids,
    companies,
    sort,
    address,
    include_matching_project_ids,
    exclude_matching_project_ids,
    ...(point && { point, radius: radius ?? 200 }),
  });
};

type Cluster = {
  count: number;
  value: string;
};

const postFilterClusters = ({
  clusterResolution,
  cellsWeNeed,
  ...props
}: ProjectFilterRequest & {
  clusterResolution:
    | 'clustering_h4'
    | 'clustering_h5'
    | 'clustering_h6'
    | 'clustering_h7'
    | 'clustering_h8'
    | 'clustering_h9'
    | 'clustering_h10';
  cellsWeNeed: string[];
  raw_queries?: RawQueries;
}): Promise<AxiosResponse<Cluster[]>> => {
  return api.post(`filter-clusters/`, {
    ...props,
    cluster_resolution: clusterResolution,
    cluster_ids: cellsWeNeed,
  });
};

export default {
  postFilterCompanies,
  postFilterProjects,
  postFilterProjectsAndDocuments,
  postFilterProjectPins,
  postFilterDocuments,
  postFilterClusters,
};
